import { BaseClient } from '../base-client';
import type {
  CommitResult,
  CreateTimerInput,
  OpenTimers,
  PatchTimerInput,
  TimerView,
  TimerWithIntervals,
} from './types';

type OpenResponse = { data: OpenTimers };
type OneView = { data: TimerView };
type OneWithIntervals = { data: TimerWithIntervals };
type OneCommit = { data: CommitResult };

export class TimersClient extends BaseClient {
  /** Open timers for the caller: the running timer (≤1) + up to
   *  `pausedLimit` paused timers, plus the total paused count. */
  listOpen(opts: { pausedLimit?: number } = {}): Promise<OpenResponse> {
    const query: Record<string, number | undefined> =
      opts.pausedLimit != null ? { pausedLimit: opts.pausedLimit } : {};
    return this.request('/api/timers/open', { query });
  }

  get(id: string): Promise<OneWithIntervals> {
    return this.request(`/api/timers/${encodeURIComponent(id)}`);
  }

  start(input: CreateTimerInput = {}): Promise<OneView> {
    return this.request('/api/timers', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchTimerInput): Promise<OneView> {
    return this.request(`/api/timers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  pause(id: string): Promise<OneView> {
    return this.request(`/api/timers/${encodeURIComponent(id)}/pause`, {
      method: 'POST',
      body: {},
    });
  }

  resume(id: string): Promise<OneView> {
    return this.request(`/api/timers/${encodeURIComponent(id)}/resume`, {
      method: 'POST',
      body: {},
    });
  }

  /** Commit a timer to a timesheet. The entry's role is the activity's own
   *  role, derived server-side, so no role hint is sent. */
  commit(id: string): Promise<OneCommit> {
    return this.request(`/api/timers/${encodeURIComponent(id)}/commit`, {
      method: 'POST',
      body: {},
    });
  }

  discard(id: string): Promise<OneView> {
    return this.request(`/api/timers/${encodeURIComponent(id)}/discard`, {
      method: 'POST',
      body: {},
    });
  }
}
